<script setup>
import { ref } from 'vue'

import { useDetectDevice } from '@/composables/device'

const props = defineProps({
  // [{ key, label, required?, internal? }] in their natural order
  columns: {
    type: Array,
    required: true,
  },
  // Selected keys in print order
  modelValue: {
    type: Array,
    required: true,
  },
  disable: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const { isMobile } = useDetectDevice()

const draggedKey = ref(null)
// Full order, unselected columns included, so unchecking a column keeps its place.
const storedOrder = ref(null)

const columnKeys = () => props.columns.map(column => column.key)
const isSelected = (column) => props.modelValue.includes(column.key)
const isSameOrder = (left, right) => left.length === right.length && left.every((key, index) => key === right[index])
const orderedKeys = () => {
  const keys = columnKeys()
  const selected = props.modelValue.filter(key => keys.includes(key))
  const stored = storedOrder.value
  if (stored?.length === keys.length && stored.every(key => keys.includes(key)) && isSameOrder(stored.filter(key => selected.includes(key)), selected)) return stored
  return [...selected, ...keys.filter(key => !selected.includes(key))]
}
const orderedColumns = () => {
  const columnsByKey = new Map(props.columns.map(column => [column.key, column]))
  return orderedKeys().map(key => columnsByKey.get(key))
}

const applyOrder = (order, selected) => {
  storedOrder.value = order
  emit('update:modelValue', order.filter(key => selected.has(key)))
}
const onChecked = (column, value) => {
  const selected = new Set(props.modelValue)
  if (value) selected.add(column.key)
  else selected.delete(column.key)
  applyOrder(orderedKeys(), selected)
}
const moveColumn = (columnKey, targetKey) => {
  const order = [...orderedKeys()]
  const from = order.indexOf(columnKey)
  const to = order.indexOf(targetKey)
  if (from < 0 || to < 0 || from === to) return
  order.splice(to, 0, order.splice(from, 1)[0])
  applyOrder(order, new Set(props.modelValue))
}
const onDropped = (targetKey) => {
  const columnKey = draggedKey.value
  draggedKey.value = null
  moveColumn(columnKey, targetKey)
}
const onShifted = (column, offset) => {
  const order = orderedKeys()
  const target = order[order.indexOf(column.key) + offset]
  if (target) moveColumn(column.key, target)
}
</script>
<template>
  <q-list dense>
    <q-item
      v-for="column in orderedColumns()"
      :key="column.key"
      :draggable="!disable && !isMobile && isSelected(column)"
      :class="{ 'column-checklist-item--draggable': !disable && !isMobile && isSelected(column) }"
      @dragstart="draggedKey = column.key"
      @dragover.prevent
      @drop="onDropped(column.key)"
    >
      <q-item-section side>
        <q-checkbox
          :model-value="isSelected(column)"
          :disable="disable || column.required"
          dense
          @update:model-value="value => onChecked(column, value)"
        />
      </q-item-section>
      <q-item-section>
        <q-item-label :class="{ 'text-grey-6': !isSelected(column) }">
          {{ column.label }}
          <q-icon
            v-if="column.internal"
            name="mdi-lock-outline"
            color="warning"
            class="q-ml-xs"
          />
        </q-item-label>
      </q-item-section>
      <q-item-section v-if="!disable && isSelected(column)" side>
        <div class="row no-wrap items-center">
          <q-icon
            v-if="!isMobile"
            name="mdi-drag"
            color="grey-6"
            class="q-mr-xs"
          />
          <q-btn
            flat
            round
            dense
            size="sm"
            icon="mdi-chevron-up"
            @click="onShifted(column, -1)"
          />
          <q-btn
            flat
            round
            dense
            size="sm"
            icon="mdi-chevron-down"
            @click="onShifted(column, 1)"
          />
        </div>
      </q-item-section>
    </q-item>
  </q-list>
</template>
<style scoped lang="scss">
.column-checklist-item--draggable {
  cursor: grab;
}
</style>
